import type { ActivityType, Entity, Language } from 'types';
import type { ListCampaign } from '../campaign/types';
import type { Sums } from '../common/types';
import type { EmbeddedUser } from 'modules/core/types';
import type { BlockStatus, BlockStatusTemplate } from '../status/types';
import type { Attachment } from 'modules/attachments/types';
import type { Topic } from 'modules/comments/types';
import type { TaskList } from 'modules/tasks/types';
import type { BudgetScope, FeeAffinity, PlanStrategyType } from '../plan/types';
import type { RadioClip } from 'modules/radio/types';
import type { MarkdownRead } from 'components/RichText';

export type PlanConfig = {
  default_plan_strategy: PlanStrategyType;
  block_status_template: BlockStatusTemplate | null;
  available_strategies: PlanStrategyType[];
  enabled: boolean;
  lock: boolean;
  asset_delivery_contact: string;
  // working days
  asset_delivery_deadline: number;
};

export type PlanningConfig = Record<ActivityType, PlanConfig>;

export type ChangeScope = 'PLAN' | 'PRICES_UPDATE' | 'PRICES' | 'BRIEF_UPDATE' | 'REJECTED_PLAN' | 'REPLAN';

export const blockStates = ['NEW', 'PLANNING', 'SUBMITTED', 'APPROVED', 'DONE', 'CANCELED'] as const;
export type BlockState = (typeof blockStates)[number];

export interface BlockSums extends Sums {
  all_bound: boolean;
}

export interface BlockBase<T extends ActivityType = ActivityType> extends Entity {
  type: T;
  state: BlockState;
  readonly status: BlockStatus;
  title: string;
  client_po_number: string | null;
  readonly final_client_po_number: string | null;
  date_from: string;
  date_to: string;
  deadline: string | null;
  code: string;
  sums: BlockSums;
}

export interface ListBlock extends BlockBase {
  campaign: ListCampaign;
  planners: EmbeddedUser[];
}

type CretiveFields = {
  first_sketch: string;
  materials: { kind: string; amount: number }[];
  styleguide: string;
  copywrite: string;
  website: string;
  social_media: string;
};

interface BlockDetailsBase<T extends ActivityType = ActivityType> extends BlockBase<T> {
  readonly available_statuses: BlockStatus[];
  readonly _revision: number;
  role: {
    manage: boolean;
    plan: boolean;
  };
  // brief fields
  planners: EmbeddedUser[];
  deadline: string;
  budget: string;
  budget_scope: BudgetScope;
  budget_fee_affinity: FeeAffinity;
  canceled_message: string | null;
  suggested_media: string;
  brief: MarkdownRead;
  // @TODO
  fields: any | CretiveFields;
  languages: Language[];
  brief_attachments: Attachment[];
  target_groups: string;
  target_group_size: string | null;

  topic: Topic;
  task_list: TaskList;

  // mobve theese to block? to
  // version
  readonly issued_at: string;
  readonly version: number;
  readonly scope: ChangeScope;
  readonly motivation: string;
  readonly brief_updated: boolean;
}

type BlockDetailsExtra<T extends ActivityType> = T extends 'RADIO'
  ? { radio_clips: RadioClip[] }
  : { radio_clips?: never };

export type BlockDetails<T extends ActivityType = ActivityType> = T extends ActivityType
  ? BlockDetailsBase<T> & BlockDetailsExtra<T>
  : never;
